import React, { useEffect, useState } from 'react';
import { useDocumentVisibility, useRequest } from 'ahooks';
import { observer } from 'mobx-react';
import { useOutletContext } from 'react-router-dom';
import { Space, Tabs } from 'antd';
import request from '@/tools/httpClient';
import { common } from '@/api';
import { CommonStore } from '@/stores';
import Webview from '@/pages/webview/';
import { UserInfo } from '@/stores/account';
import { getDecrptedURL } from '@/components/QuckBI';
import TreeSelect, { OrganizationTreeTogetherBO } from './TreeSelect';
import './index.less';

interface OutletProps {
  userInfo: UserInfo;
}

const TAB_KEY_MAP = {
  ALL: '1',
  MY_DEPARTMENT: '2',
  MY_SELF: '3',
};

const TAB_MAP = {
  [TAB_KEY_MAP.ALL]: {
    key: TAB_KEY_MAP.ALL,
    label: '全部数据',
  },
  [TAB_KEY_MAP.MY_DEPARTMENT]: {
    key: TAB_KEY_MAP.MY_DEPARTMENT,
    label: '我的部门数据',
  },
  [TAB_KEY_MAP.MY_SELF]: {
    key: TAB_KEY_MAP.MY_SELF,
    label: '我负责的数据',
  },
};

const CustomerBoard: React.FC = () => {
  const [url, setUrl] = useState('');
  const documentVisibility = useDocumentVisibility();
  const { userInfo } = useOutletContext<OutletProps>();
  const [activeTab, setActiveTab] = useState(TAB_KEY_MAP.MY_SELF);
  const [employeeIdList, setEmployeeIdList] = useState<string[]>([]);
  const [tabsItems, setTabsItems] = useState([TAB_MAP[TAB_KEY_MAP.MY_SELF]]);

  const { run: getBoard } = useRequest(
    async () => {
      const { panelUrl, expireTime } = await CommonStore.completedUrl({
        panelNo: 'p_customer_dashboard',
        employeeIdList,
      });
      panelUrl && setUrl(panelUrl);
      expireTime &&
        localStorage.setItem(
          'p_customer_dashboard_expire_time',
          (expireTime * 60 * 1000 + Date.now()).toString(),
        );
    },
    {
      debounceWait: 300,
      refreshDeps: [employeeIdList],
    },
  );

  useEffect(() => {
    if (documentVisibility === 'visible') {
      const expireTime = localStorage.getItem('p_customer_dashboard_expire_time');

      if (expireTime && Date.now() > parseInt(expireTime, 10)) {
        getBoard();
      }
    }
  }, [documentVisibility]);

  useRequest(
    async () => {
      const isDeptHead = await request.get(common.isDeptHead).then((res) => res.data);

      const list: Array<{ key: string; label: string }> = [];
      if (userInfo?.adminFlag || userInfo?.isSupervisorAuth) {
        list.push(TAB_MAP[TAB_KEY_MAP.ALL]);
        setActiveTab(TAB_KEY_MAP.ALL);
      }

      if (isDeptHead) {
        list.push(TAB_MAP[TAB_KEY_MAP.MY_DEPARTMENT]);
        !list.length && setActiveTab(TAB_KEY_MAP.MY_DEPARTMENT);
      }

      list.push(TAB_MAP[TAB_KEY_MAP.MY_SELF]);
      !list.length && setActiveTab(TAB_KEY_MAP.MY_SELF);

      setTabsItems(list);
    },
    {
      refreshDeps: [userInfo],
    },
  );

  const { data: dataSource } = useRequest(
    async () => {
      if (!userInfo || activeTab === TAB_KEY_MAP.MY_SELF) {
        setEmployeeIdList([]);
        return [];
      }
      const res = await request
        .get(common.memberOrganization, {
          params: {
            // 1-全部，2-我的部门数据
            type: activeTab === TAB_KEY_MAP.ALL ? 1 : 2,
          },
        })
        .then((res) => res.data);
      return (res ? [res] : []) as OrganizationTreeTogetherBO[];
    },
    {
      manual: false,
      refreshDeps: [userInfo, activeTab],
    },
  );

  
  return (
    <div className="customer-board">
      <div className="query">
        <Tabs
          activeKey={activeTab}
          onChange={(key) => setActiveTab(key as string)}
          type="card"
          items={tabsItems}
        />
        {activeTab === TAB_KEY_MAP.MY_SELF ? null : (
          <Space>
            <div className="query-title">数据筛选</div>
            <TreeSelect
              value={employeeIdList}
              dataSource={dataSource || []}
              onChange={setEmployeeIdList}
            />
          </Space>
        )}
      </div>
      <Webview key={url} url={getDecrptedURL(url)} />
    </div>
  );
};

export default observer(CustomerBoard);
